import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users, Search, Eye, Plus, ChevronUp, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { clientesService } from '../services/api';
import { formatCurrency } from '../utils/formatters';

const Clientes = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAtivo, setFilterAtivo] = useState('all');
  const [sortField, setSortField] = useState('nome_cliente');
  const [sortDirection, setSortDirection] = useState('asc');

  const { data: clientesResponse, isLoading, error } = useQuery({
    queryKey: ['clientes', { search: searchTerm, ativo: filterAtivo }],
    queryFn: () => clientesService.list({ 
      search: searchTerm, 
      ativo: filterAtivo === 'all' ? undefined : filterAtivo === 'true'
    }),
    staleTime: 30000,
  });

  // Extrair os resultados da resposta - corrigir para lidar com diferentes estruturas
  const rawClientes = useMemo(() => {
    if (!clientesResponse) return [];
    
    // Se for um array direto
    if (Array.isArray(clientesResponse)) {
      return clientesResponse;
    }
    
    // Se for um objeto com results (paginação)
    if (clientesResponse.results && Array.isArray(clientesResponse.results)) {
      return clientesResponse.results;
    }
    
    // Se for um objeto com data
    if (clientesResponse.data && Array.isArray(clientesResponse.data)) {
      return clientesResponse.data;
    }
    
    // Fallback
    return [];
  }, [clientesResponse]);

  // Ordenar clientes no frontend
  const filteredClientes = useMemo(() => {
    if (!rawClientes.length) return [];
    
    return [...rawClientes].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      // Tratar valores nulos/undefined
      if (aValue === null || aValue === undefined) aValue = 0;
      if (bValue === null || bValue === undefined) bValue = 0;
      
      // Converter para números se necessário
      if (typeof aValue === 'string' && !isNaN(aValue)) aValue = parseFloat(aValue);
      if (typeof bValue === 'string' && !isNaN(bValue)) bValue = parseFloat(bValue);
      
      // Comparar valores
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rawClientes, sortField, sortDirection]);

  // Função para ordenar colunas
  const handleSort = (field) => {
    if (sortField === field) {
      // Se clicar na mesma coluna, inverte a direção
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Se clicar em uma nova coluna, define como ascendente
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Função para renderizar o ícone de ordenação
  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return <ChevronUp className="h-4 w-4 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ChevronUp className="h-4 w-4 text-primary-600" />
      : <ChevronDown className="h-4 w-4 text-primary-600" />;
  };

  // Função para renderizar cabeçalho clicável
  const renderSortableHeader = (field, label) => (
    <th 
      className={`px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors ${
        sortField === field ? 'bg-blue-50 dark:bg-blue-900/20' : ''
      }`}
      onClick={() => handleSort(field)}
      title={`Clique para ordenar por ${label}`}
    >
      <div className="flex items-center justify-between">
        <span className={sortField === field ? 'font-bold text-blue-700 dark:text-blue-300' : ''}>
          {label}
        </span>
        <div className="flex flex-col">
          <ChevronUp className={`h-3 w-3 ${sortField === field && sortDirection === 'asc' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`} />
          <ChevronDown className={`h-3 w-3 ${sortField === field && sortDirection === 'desc' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`} />
        </div>
      </div>
    </th>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Clientes</h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
            Gerencie seus clientes e visualize informações de cobrança
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <Link
            to="/clientes/novo"
            className="btn-primary inline-flex items-center"
          >
            <Plus className="h-4 w-4 mr-2" />
            Novo Cliente
          </Link>
        </div>
      </div>

      {/* Filtros */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar clientes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10"
              />
            </div>
          </div>
          <div className="sm:w-48">
            <select
              value={filterAtivo}
              onChange={(e) => setFilterAtivo(e.target.value)}
              className="input"
            >
              <option value="all">Todos os Status</option>
              <option value="true">Ativos</option>
              <option value="false">Inativos</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lista de Clientes */}
      <div className="card">
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center text-red-600 dark:text-red-400">
            Erro ao carregar clientes
          </div>
        ) : filteredClientes.length === 0 ? (
          <div className="text-center py-8">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">Nenhum cliente encontrado</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            {/* Indicador de ordenação */}
            {sortField && (
              <div className="mb-3 text-sm text-gray-600 dark:text-gray-400">
                <span className="font-medium">Ordenado por:</span> {sortField === 'nome_cliente' ? 'Cliente' : 
                  sortField === 'total_contas_abertas' ? 'Contas em Aberto' :
                  sortField === 'total_valor_aberto' ? 'Valor em Aberto' :
                  sortField === 'contas_vencidas' ? 'Contas Vencidas' : sortField} 
                ({sortDirection === 'asc' ? 'Crescente' : 'Decrescente'})
              </div>
            )}
            
            {/* Tabela de Clientes */}
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    {renderSortableHeader('nome_cliente', 'Cliente')}
                    {renderSortableHeader('total_contas_abertas', 'Contas')}
                    {renderSortableHeader('total_valor_aberto', 'Valor')}
                    {renderSortableHeader('valor_em_atraso', 'Atraso')}
                    {renderSortableHeader('contas_vencidas', 'Vencidas')}
                    {renderSortableHeader('percentual_inadimplencia', 'Inadimplência')}
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredClientes.map((cliente) => (
                    <tr key={cliente.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-2 py-2">
                        <div className="min-w-0 max-w-32">
                          <p className="text-xs font-medium text-gray-900 dark:text-white truncate" title={cliente.nome_cliente}>
                            {cliente.nome_cliente}
                          </p>
                          {cliente.email && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate" title={cliente.email}>
                              {cliente.email}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <span className="text-xs text-gray-900 dark:text-white">
                          {cliente.total_contas_abertas || 0}
                        </span>
                      </td>
                      <td className="px-2 py-2">
                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                          {formatCurrency(cliente.total_valor_aberto || 0)}
                        </span>
                      </td>
                      <td className="px-2 py-2">
                        <span className={`text-xs font-medium ${
                          (cliente.valor_em_atraso || 0) > 0 
                            ? 'text-red-600 dark:text-red-400' 
                            : 'text-gray-500 dark:text-gray-400'
                        }`}>
                          {formatCurrency(cliente.valor_em_atraso || 0)}
                        </span>
                      </td>
                      <td className="px-2 py-2">
                        <span className={`text-xs ${
                          (cliente.contas_vencidas || 0) > 0 
                            ? 'text-red-600 dark:text-red-400 font-medium' 
                            : 'text-gray-500 dark:text-gray-400'
                        }`}>
                          {cliente.contas_vencidas || 0}
                        </span>
                      </td>
                      <td className="px-2 py-2">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                          (cliente.percentual_inadimplencia || 0) > 80 
                            ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300' 
                            : (cliente.percentual_inadimplencia || 0) > 50
                            ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300'
                            : (cliente.percentual_inadimplencia || 0) > 20
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300'
                            : 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                        }`}>
                          {(cliente.percentual_inadimplencia || 0).toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-2 py-2">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                          cliente.ativo 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300' 
                            : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
                        }`}>
                          {cliente.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-2 py-2">
                        <Link
                          to={`/clientes/${cliente.id}`}
                          className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/40 transition-colors"
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          Ver
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Informações de total */}
        {filteredClientes.length > 0 && (
          <div className="mt-4 text-sm text-gray-500 dark:text-gray-400 text-center">
            Total de {filteredClientes.length} clientes
          </div>
        )}

        {/* Legenda de Inadimplência */}
        <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
            <strong>Níveis de Inadimplência:</strong>
          </p>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300">
              ≤ 20% - Baixa
            </span>
            <span className="inline-flex items-center px-2 py-1 rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300">
              21-50% - Média
            </span>
            <span className="inline-flex items-center px-2 py-1 rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300">
              51-80% - Alta
            </span>
            <span className="inline-flex items-center px-2 py-1 rounded-full bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300">
              {'>'} 80% - Crítica
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Clientes; 